import { useEffect, useMemo, useState } from 'react';
import { NavLink, useSearchParams } from 'react-router-dom';
import type { SessionUser } from '../lib/api';
import type { OutfitRecommendation, Mood } from '../../shared/recommendations';
import { MOODS } from '../../shared/recommendations';
import {
  GARMENT_CATEGORIES,
  OCCASIONS,
  type GarmentCategory,
  type Occasion,
  type WardrobeItem,
} from '../../shared/wardrobe';
import { listWardrobeItems } from '../lib/items';
import { getRecommendations, getWearLogs, wearToday } from '../lib/recommendations';
import { Icon } from './Icon';

const greeting = () => {
  const hour = new Date().getHours();
  return hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';
};

function RecommendationCard({
  recommendation,
  active,
  onChoose,
}: {
  recommendation: OutfitRecommendation;
  active: boolean;
  onChoose: () => void;
}) {
  return (
    <button
      aria-pressed={active}
      className={`suggestion-card${active ? ' is-active' : ''}`}
      type="button"
      onClick={onChoose}
    >
      <span className="suggestion-images">
        {recommendation.garments.map((garment) => (
          <img alt="" key={garment.id} src={garment.photoUrl} />
        ))}
      </span>
      <span>
        <strong>{recommendation.garments.map((garment) => garment.name).join(' + ')}</strong>
        <small>{active ? 'Selected look' : 'Choose this alternative'}</small>
      </span>
    </button>
  );
}

export function TodayRecommendationsPage({
  user,
  styleMode = false,
}: {
  user: SessionUser;
  styleMode?: boolean;
}) {
  const [params] = useSearchParams();
  const [wardrobe, setWardrobe] = useState<WardrobeItem[]>([]);
  const [occasion, setOccasion] = useState<Occasion>('Everyday');
  const [mood, setMood] = useState<Mood>('Comfortable');
  const [lockedIds, setLockedIds] = useState<string[]>(() =>
    params.get('item') ? [params.get('item') ?? ''] : [],
  );
  const [missingCategories, setMissingCategories] = useState<GarmentCategory[]>([]);
  const [recommendations, setRecommendations] = useState<OutfitRecommendation[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [recent, setRecent] = useState<Awaited<ReturnType<typeof getWearLogs>>>([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    void Promise.all([listWardrobeItems(), getWearLogs()])
      .then(([items, logs]) => {
        setWardrobe(items);
        setRecent(logs);
      })
      .catch((caught: unknown) =>
        setMessage(caught instanceof Error ? caught.message : 'Your closet could not load.'),
      )
      .finally(() => setLoading(false));
  }, []);

  const selected = recommendations[selectedIndex];
  const availableMissing = useMemo(
    () =>
      GARMENT_CATEGORIES.filter((category) =>
        wardrobe.some((garment) => garment.category === category),
      ),
    [wardrobe],
  );

  function toggleLocked(id: string) {
    setLockedIds((current) =>
      current.includes(id) ? current.filter((entry) => entry !== id) : [...current, id],
    );
    setRecommendations([]);
  }

  async function generate() {
    setGenerating(true);
    setMessage('');
    try {
      const result = await getRecommendations({
        occasion,
        mood,
        lockedGarmentIds: lockedIds,
        missingCategories,
      });
      setRecommendations(result.recommendations);
      setSelectedIndex(0);
      if (!result.recommendations.length) {
        setMessage('Add a few matching categories to your closet, then try again.');
      }
    } catch (caught) {
      setMessage(caught instanceof Error ? caught.message : 'Suggestions could not load.');
    } finally {
      setGenerating(false);
    }
  }

  function swapGarment(garment: WardrobeItem) {
    if (!selected || lockedIds.includes(garment.id)) return;
    const replacement = recommendations
      .flatMap((recommendation) => recommendation.garments)
      .find(
        (candidate) =>
          candidate.category === garment.category &&
          candidate.id !== garment.id &&
          !selected.garments.some((current) => current.id === candidate.id),
      );
    if (!replacement) {
      setMessage(`No other ${garment.category.toLocaleLowerCase()} match this request yet.`);
      return;
    }
    const updated = {
      ...selected,
      id: `${selected.id}:swap:${replacement.id}`,
      garments: selected.garments.map((current) =>
        current.id === garment.id ? replacement : current,
      ),
      explanation: `${replacement.name} gives this look a fresh alternative while every locked piece stays unchanged.`,
    };
    setRecommendations((current) =>
      current.map((recommendation, index) => (index === selectedIndex ? updated : recommendation)),
    );
  }

  async function markWorn() {
    if (!selected) return;
    setMessage('');
    try {
      await wearToday(selected, occasion, mood);
      setRecent(await getWearLogs());
      setMessage('Saved as today’s outfit ♡');
    } catch (caught) {
      setMessage(caught instanceof Error ? caught.message : 'Wear today could not save.');
    }
  }

  if (loading) {
    return (
      <div className="page">
        <p>Opening your wardrobe…</p>
      </div>
    );
  }

  return (
    <div className="page recommendations-page">
      <header className="page-header">
        <div>
          <p className="eyebrow">{styleMode ? 'Style with an item' : 'Today'}</p>
          <h1>{styleMode ? 'Complete a look' : `${greeting()}, ${user.displayName}`}</h1>
          <p>
            {styleMode
              ? 'Lock the pieces you want to keep, then choose what the look still needs.'
              : 'Choose the feeling and occasion. Every suggestion comes only from your closet.'}
          </p>
        </div>
        {!styleMode ? (
          <NavLink className="button button--secondary" to="/style">
            Style with an item
          </NavLink>
        ) : null}
      </header>

      {wardrobe.length === 0 ? (
        <section className="empty-state">
          <span className="empty-state__icon">
            <Icon name="closet" size={34} />
          </span>
          <h2>Your first suggestion starts with your closet</h2>
          <p>Add a few real pieces, then this page will combine only what you own.</p>
          <NavLink className="button button--primary" to="/add">
            Add a garment
          </NavLink>
        </section>
      ) : (
        <>
          <section className="styling-controls">
            <fieldset className="choice-fieldset">
              <legend>Occasion</legend>
              <div className="choice-chips">
                {OCCASIONS.map((value) => (
                  <button
                    aria-pressed={occasion === value}
                    className={occasion === value ? 'is-selected' : ''}
                    key={value}
                    type="button"
                    onClick={() => setOccasion(value)}
                  >
                    {value}
                  </button>
                ))}
              </div>
            </fieldset>
            <fieldset className="choice-fieldset">
              <legend>Mood</legend>
              <div className="choice-chips">
                {MOODS.map((value) => (
                  <button
                    aria-pressed={mood === value}
                    className={mood === value ? 'is-selected' : ''}
                    key={value}
                    type="button"
                    onClick={() => setMood(value)}
                  >
                    {value}
                  </button>
                ))}
              </div>
            </fieldset>

            {styleMode ? (
              <>
                <div className="style-lock-grid">
                  {wardrobe.map((garment) => (
                    <button
                      aria-pressed={lockedIds.includes(garment.id)}
                      className={lockedIds.includes(garment.id) ? 'is-locked' : ''}
                      key={garment.id}
                      type="button"
                      onClick={() => toggleLocked(garment.id)}
                    >
                      <img alt="" src={garment.photoUrl} />
                      <span>
                        <strong>{garment.name}</strong>
                        <small>{lockedIds.includes(garment.id) ? 'Locked' : 'Tap to lock'}</small>
                      </span>
                    </button>
                  ))}
                </div>
                <fieldset className="choice-fieldset">
                  <legend>
                    What should we add? <small>Leave empty to choose automatically</small>
                  </legend>
                  <div className="choice-chips">
                    {availableMissing.map((value) => (
                      <button
                        aria-pressed={missingCategories.includes(value)}
                        className={missingCategories.includes(value) ? 'is-selected' : ''}
                        key={value}
                        type="button"
                        onClick={() =>
                          setMissingCategories((current) =>
                            current.includes(value)
                              ? current.filter((entry) => entry !== value)
                              : [...current, value],
                          )
                        }
                      >
                        {value}
                      </button>
                    ))}
                  </div>
                </fieldset>
              </>
            ) : null}
            <button
              className="button button--primary"
              disabled={generating}
              type="button"
              onClick={() => void generate()}
            >
              <Icon name="sparkles" size={18} />
              {generating
                ? 'Styling your closet…'
                : recommendations.length
                  ? 'Try another set'
                  : 'Create suggestions'}
            </button>
          </section>

          {message ? (
            <p className="recommendation-message" role="status">
              {message}
            </p>
          ) : null}

          {selected ? (
            <section className="recommendation-results">
              <article className="primary-suggestion">
                <div className="primary-suggestion__images">
                  {selected.garments.map((garment) => (
                    <div key={garment.id}>
                      <img alt={garment.name} src={garment.photoUrl} />
                      <button
                        disabled={lockedIds.includes(garment.id)}
                        type="button"
                        onClick={() => swapGarment(garment)}
                      >
                        {lockedIds.includes(garment.id) ? 'Locked' : 'Swap'}
                      </button>
                    </div>
                  ))}
                </div>
                <div className="primary-suggestion__copy">
                  <p className="eyebrow">Why this works</p>
                  <h2>
                    {mood} for {occasion.toLocaleLowerCase()}
                  </h2>
                  <p>{selected.explanation}</p>
                  <button
                    className="button button--primary"
                    type="button"
                    onClick={() => void markWorn()}
                  >
                    Wear this today
                  </button>
                </div>
              </article>
              {recommendations.length > 1 ? (
                <div className="suggestion-alternatives">
                  {recommendations.map((recommendation, index) => (
                    <RecommendationCard
                      active={index === selectedIndex}
                      key={recommendation.id}
                      recommendation={recommendation}
                      onChoose={() => setSelectedIndex(index)}
                    />
                  ))}
                </div>
              ) : null}
            </section>
          ) : null}

          {recent.length ? (
            <section className="recent-worn">
              <div>
                <p className="eyebrow">Recently worn</p>
                <h2>Your latest looks</h2>
              </div>
              <div>
                {recent.slice(0, 7).map((log) => (
                  <article key={log.id}>
                    <span>
                      {log.garments.map((garment) => (
                        <img alt="" key={garment.id} src={garment.photoUrl} />
                      ))}
                    </span>
                    <strong>
                      {new Date(`${log.wornOn}T00:00:00`).toLocaleDateString(undefined, {
                        month: 'short',
                        day: 'numeric',
                      })}
                    </strong>
                  </article>
                ))}
              </div>
            </section>
          ) : null}
        </>
      )}
    </div>
  );
}
